/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { SecurityHelper } from 'sarsaparilla';
import { gaSetRemoveFromCartEvent } from 'shared-ui/utils/gaCommon';
import {
    PENDING_MODIFICATION,
    RESERVED,
} from 'ui-camping/src/shared/constants/reservation';
import { authCampingAxios } from 'ui-camping/src/shared/utils/campingAxios';
import axios from 'axios';
import * as types from '../constants';

export function fetchCart() {
    return (dispatch) =>
        axios
            .get(`${process.env.API}/cart/shoppingcart`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.GET_CART, payload: response.data });
            })
            .catch(() => {
                dispatch({
                    type: types.CART_ERROR,
                    payload: 'Error fetching cart',
                    error: true,
                });
            });
}

export function setCart(cartData) {
    return { type: types.GET_CART, payload: cartData };
}

async function getReservationStatus(reservationID) {
    const response = await authCampingAxios.get(`/reservations/${reservationID}`);

    return response.data.reservation?.reservation_status;
}

/**
 * Reverts a camping reservation that is still pending a modification. Resolves
 * when nothing is pending, so removing an item that CampingService has already
 * settled never issues a destructive delete against a live reservation.
 */
async function discardPendingCampingModification(reservationID) {
    if ((await getReservationStatus(reservationID)) !== PENDING_MODIFICATION) {
        return;
    }

    try {
        await authCampingAxios.delete(`/reservations/${reservationID}`);
    } catch (error) {
        // CartService may have reverted the modification already, which makes
        // our delete fail even though the reservation is back to its original state.
        if ((await getReservationStatus(reservationID)) !== RESERVED) {
            throw error;
        }
    }
}

export function removeReservation(
    reservationID,
    { discardCampingModification = false } = {}
) {
    return async (dispatch) => {
        let response;

        try {
            response = await axios.delete(
                `${process.env.API}/cart/shoppingcart/${reservationID}`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            );
        } catch {
            dispatch({
                type: types.REMOVE_RESERVATION_ERROR,
                payload: 'Error removing item from cart',
            });
            return;
        }

        let discardFailed = false;

        if (discardCampingModification) {
            try {
                await discardPendingCampingModification(reservationID);
            } catch {
                discardFailed = true;
            }
        }

        gaSetRemoveFromCartEvent();

        // CartService keeps reporting the removed reservation as the cart's pending
        // modification, so drop that stale block - but only when it names the
        // reservation we just reverted, never some other pending modification.
        const clearsStaleModification =
            discardCampingModification &&
            !discardFailed &&
            response.data?.modification?.reservation_id === reservationID;

        // The item is gone from CartService either way, so always publish the new
        // cart rather than leaving Redux holding the stale, pre-removal state.
        dispatch({
            type: types.GET_CART,
            payload: clearsStaleModification
                ? { ...response.data, modification: null }
                : response.data,
        });

        if (discardFailed) {
            dispatch({
                type: types.REMOVE_RESERVATION_ERROR,
                payload:
                    'Error canceling your reservation change. Please refresh the page and try again.',
            });
        }
    };
}

export function extendTimer() {
    return (dispatch) =>
        axios
            .patch(
                `${process.env.API}/cart/shoppingcart/expiration`,
                {},
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.GET_CART, payload: response.data });
            })
            .catch(() => {
                dispatch({
                    type: types.EXTEND_EXPIRATION_ERROR,
                    payload: 'Error extending expiration',
                });
            });
}

export function fetchCanBuyNow() {
    return () =>
        axios
            .get(`${process.env.API}/cart/buy-now`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => response.data.can_buy_now)
            .catch(() => false);
}

// cSpell:ignore shoppingcart
